/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Modal, Button,
} from 'antd';
import { CheckCircleOutlined, PaperClipOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getJustificacionesGrupalesReloj } from 'api/configuraciones/clock/Justificacion/JustificacionGrupal';
import { ESTATUS_ACTIVO } from 'utils/constants';

const ESTADOS_LABEL = {
  1: 'Activo',
  4: 'Autorizada',
  5: 'Cancelada',
  6: 'Rechazada',
};

const nombreDocumentoOficio = (documento) => {
  const nombre = documento?.nombre_del_documento || '';
  const extension = documento?.extension_del_documento?.toLowerCase() || '';
  if (extension && nombre.toLowerCase().endsWith(`.${extension}`)) return nombre;
  return extension ? `${nombre}.${extension}` : nombre;
};

const CampoDetalle = ({ label, value }) => (
  <Col xs={24} sm={12} md={8} style={{ marginBottom: 12 }}>
    <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>{label}</div>
    <div style={{
      border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 11px', minHeight: 32,
    }}
    >
      {value || '-'}
    </div>
  </Col>
);

const detalleColumns = [
  {
    titleText: 'Empleado', dataIndex: 'empleado_nombre', key: 'empleado_nombre', width: 230,
  },
  {
    titleText: 'Puesto', dataIndex: 'puesto', key: 'puesto', width: 160,
  },
  {
    titleText: 'Plaza', dataIndex: 'plaza', key: 'plaza', width: 160,
  },
  {
    titleText: 'Fecha', dataIndex: 'fecha', key: 'fecha', width: 100,
  },
  {
    titleText: 'Estatus',
    dataIndex: 'estados_globales',
    key: 'estados_globales',
    width: 100,
    render: (value) => ESTADOS_LABEL[value] || value,
  },
];

const JustificacionGrupalGeneral = ({ permission, changeToForms, refreshFlag }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [detalleVisible, setDetalleVisible] = useState(false);
  const [detalleRecord, setDetalleRecord] = useState({});

  const fetchData = async () => {
    setLoading(true);
    const response = await getJustificacionesGrupalesReloj();
    setData(response || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [refreshFlag]);

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
  };

  const verDetalle = (record) => {
    // El doble clic en la fila pasa el registro directamente; el botón
    // "Vista completa" del Table invoca este callback sin argumentos y
    // depende del registro ya elegido por la selección de la fila (radio).
    setDetalleRecord(record || selectedData);
    setDetalleVisible(true);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'No. de Oficio', dataIndex: 'numero_oficio', key: 'numero_oficio', width: 160,
    },
    {
      titleText: 'Fecha de Oficio', dataIndex: 'fecha_oficio', key: 'fecha_oficio', width: 140,
    },
    {
      titleText: 'Área que Expide',
      dataIndex: 'area_que_expide',
      key: 'area_que_expide',
      width: 220,
      render: (value) => value?.nombre,
    },
    {
      titleText: 'Tipo de Evento',
      dataIndex: 'tipo_de_evaluacion_de_asistencia',
      key: 'tipo_de_evaluacion_de_asistencia',
      width: 180,
      render: (value) => value?.nombre,
    },
    {
      titleText: 'Periodo Desde', dataIndex: 'fecha_evento_desde', key: 'fecha_evento_desde', width: 140,
    },
    {
      titleText: 'Periodo Hasta', dataIndex: 'fecha_evento_hasta', key: 'fecha_evento_hasta', width: 140,
    },
    {
      titleText: 'Total Empleados', dataIndex: 'total_empleados', key: 'total_empleados', width: 140,
    },
    {
      titleText: 'Emisor',
      dataIndex: 'empleado_emisor',
      key: 'empleado_emisor',
      width: 220,
      render: (value) => value?.numero_y_nombre_completo,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 140,
      render: (value) => ESTADOS_LABEL[value?.id] || value?.descripcion,
    },
  ];

  const onAutRechazar = () => {
    const [key] = selectedRowKeys;
    changeToForms(key, true);
  };

  const onClickAdd = () => {
    changeToForms(null, false);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          mobileCols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          permission={permission}
          allowExportPDF={false}
          controls={{ onClickAdd, onClickExpand: verDetalle }}
          customActions={[
            {
              onClick: onAutRechazar,
              icon: <CheckCircleOutlined />,
              text: 'Autorizar/Rechazar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
            },
          ]}
        />
      </Spin>

      <Modal
        title={`Detalle de Justificación Grupal${detalleRecord?.numero_oficio ? ` - ${detalleRecord.numero_oficio}` : ''}`}
        open={detalleVisible}
        onCancel={() => setDetalleVisible(false)}
        footer={[
          <Button key="cerrar" onClick={() => setDetalleVisible(false)}>Cerrar</Button>,
        ]}
        width="80%"
        centered
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Row gutter={10}>
          <CampoDetalle label="Número de Oficio" value={detalleRecord?.numero_oficio} />
          <CampoDetalle label="Fecha de Oficio" value={detalleRecord?.fecha_oficio} />
          <CampoDetalle label="Área que Expide" value={detalleRecord?.area_que_expide?.nombre} />
          <CampoDetalle label="Tipo de Evento" value={detalleRecord?.tipo_de_evaluacion_de_asistencia?.nombre} />
          <CampoDetalle
            label="Periodo del Evento"
            value={
              detalleRecord?.fecha_evento_desde && detalleRecord?.fecha_evento_hasta
                ? `${detalleRecord.fecha_evento_desde} a ${detalleRecord.fecha_evento_hasta}`
                : null
            }
          />
          <CampoDetalle
            label="Estatus"
            value={
              ESTADOS_LABEL[detalleRecord?.estados_globales?.id]
              || detalleRecord?.estados_globales?.descripcion
            }
          />
          <CampoDetalle label="Total Empleados" value={detalleRecord?.total_empleados} />
          <CampoDetalle label="Emisor del Oficio" value={detalleRecord?.empleado_emisor?.numero_y_nombre_completo} />
          {!!detalleRecord?.documento_oficio && (
            <Col xs={24} sm={12} md={8} style={{ marginBottom: 12 }}>
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>
                Documento del Oficio
              </div>
              <div style={{
                border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 11px', minHeight: 32,
              }}
              >
                <a href={detalleRecord.documento_oficio.archivo} target="_blank" rel="noreferrer">
                  <PaperClipOutlined />
                  {' '}
                  {nombreDocumentoOficio(detalleRecord.documento_oficio)}
                </a>
              </div>
            </Col>
          )}
          {!!detalleRecord?.motivo_rechazo && (
            <CampoDetalle label="Motivo de Rechazo" value={detalleRecord.motivo_rechazo} />
          )}
          <Col xs={24} sm={16} style={{ marginBottom: 12 }}>
            <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>Justificación / Motivo del Oficio</div>
            <div style={{
              border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 11px', minHeight: 32,
            }}
            >
              {detalleRecord?.justificacion || '-'}
            </div>
          </Col>
          {!!detalleRecord?.empleado_autorizado_rechazado?.id && (
            <Col xs={24} sm={8} style={{ marginBottom: 12 }}>
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>
                Autorizó / Rechazó
              </div>
              <div style={{
                border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 11px', minHeight: 32,
              }}
              >
                {detalleRecord.empleado_autorizado_rechazado?.numero_y_nombre_completo || '-'}
              </div>
            </Col>
          )}
        </Row>
        <Table
          allowColumnSelector={false}
          cols={detalleColumns}
          data={detalleRecord?.detalles || []}
          allowExportParam={false}
          allowSearch={false}
        />
      </Modal>
    </Row>
  );
};

export default JustificacionGrupalGeneral;
